import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { branchesAPI } from '../api';
import { useAuth, ScopedBranch } from './AuthContext';

const STORAGE_KEY = 'branch_scope';
const STORAGE_USER_KEY = 'branch_scope_user';

export type BranchSelection = number | 'all';

interface BranchScopeContextType {
  /** Branches the current user may act on. */
  branches: ScopedBranch[];
  selectedBranchId: BranchSelection;
  setSelectedBranchId: (value: BranchSelection) => void;
  /** Ready to drop into API params: '' means "no branch filter". */
  branchParam: string;
  /** True when the user can actually switch between branches. */
  isMultiBranch: boolean;
  isLoading: boolean;
}

const BranchScopeContext = createContext<BranchScopeContextType | undefined>(undefined);

const readStoredSelection = (userId?: number): BranchSelection | null => {
  if (!userId || localStorage.getItem(STORAGE_USER_KEY) !== String(userId)) {
    return null;
  }

  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  if (raw === 'all') return 'all';
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const BranchScopeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [selectedBranchId, setSelected] = useState<BranchSelection>('all');

  // Branch-scoped users get their list straight from the auth payload. Org- and
  // global-scoped users (and older backends that send neither field) fall back to
  // GET /branches, which the server already filters by organization.
  const hasEmbeddedBranches = Array.isArray(user?.branches) && user!.branches!.length > 0;
  const needsFetch = isAuthenticated && !hasEmbeddedBranches;

  const { data: branchesData, isLoading } = useQuery({
    queryKey: ['branch-scope-branches', user?.id],
    queryFn: () => branchesAPI.list(),
    enabled: needsFetch,
    staleTime: 5 * 60 * 1000,
  });

  const branches: ScopedBranch[] = useMemo(() => {
    if (hasEmbeddedBranches) {
      return user!.branches!;
    }

    const raw = branchesData?.data;
    const list = Array.isArray(raw) ? raw : raw?.data ?? [];

    return list.map((branch: any) => ({ id: branch.id, name: branch.name }));
  }, [hasEmbeddedBranches, user, branchesData]);

  const isMultiBranch = branches.length > 1;

  const setSelectedBranchId = useCallback((value: BranchSelection) => {
    setSelected(value);
    localStorage.setItem(STORAGE_KEY, String(value));
    if (user?.id) {
      localStorage.setItem(STORAGE_USER_KEY, String(user.id));
    }
  }, [user?.id]);

  // Validate the stored selection against what this user may actually see, and reset
  // whenever the identity changes (login as someone else, logout).
  useEffect(() => {
    if (!isAuthenticated) {
      setSelected('all');
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_USER_KEY);
      return;
    }

    if (user?.id && localStorage.getItem(STORAGE_USER_KEY) !== String(user.id)) {
      setSelected('all');
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_USER_KEY);
    }

    if (branches.length === 0) return;

    const allowed = new Set(branches.map((branch) => branch.id));
    const stored = readStoredSelection(user?.id);

    if (stored !== null && (stored === 'all' || allowed.has(stored))) {
      setSelected(stored);
      return;
    }

    const fallback: BranchSelection =
      user?.branch_id && allowed.has(user.branch_id) ? user.branch_id : 'all';

    setSelected(fallback);
    localStorage.setItem(STORAGE_KEY, String(fallback));
    if (user?.id) {
      localStorage.setItem(STORAGE_USER_KEY, String(user.id));
    }
  }, [isAuthenticated, user?.id, user?.branch_id, branches]);

  const value: BranchScopeContextType = {
    branches,
    selectedBranchId,
    setSelectedBranchId,
    branchParam: selectedBranchId === 'all' ? '' : String(selectedBranchId),
    isMultiBranch,
    isLoading: needsFetch && isLoading,
  };

  return <BranchScopeContext.Provider value={value}>{children}</BranchScopeContext.Provider>;
};

export const useBranchScope = () => {
  const context = useContext(BranchScopeContext);
  if (context === undefined) {
    throw new Error('useBranchScope must be used within BranchScopeProvider');
  }
  return context;
};
